import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Table from "@/components/ui/shared/table/Table";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableRow from "@/components/ui/shared/table/TableRow";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableEdit from "@/components/settings/TableEdit";
import TagEdit from "@/components/settings/TagEdit";
import RespostasEdit from "@/components/settings/respostasEdit";
import EquipeEdit from "@/components/settings/EquipeEdit";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EtapaOptions, PipelineOptions, TagOptions } from "@/data/SelectOptions";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
// import bcrypt from "bcryptjs"; // Não mais necessário - usando Supabase Auth
import ConnectionTab from "@/components/settings/ConnectionTab";
import IntegrationsTab from "@/components/settings/IntegrationsTab";
import { PipelinesTab } from "@/components/settings/PipelinesTab";
import CustomFieldsTab from "@/components/settings/CustomFieldsTab";
import TagsTab from "@/components/settings/TagsTab";
import QuickRepliesTab from "@/components/settings/QuickRepliesTab";
import TeamTab from "@/components/settings/TeamTab";
import WhatsAppTemplatesTab from "@/components/settings/WhatsAppTemplatesTab";
import LogsTab from "@/components/settings/LogsTab";
import ResetTab from "@/components/settings/ResetTab";
import LossReasonsTab from "@/components/settings/LossReasonsTab";
import NotificationsTab from "@/components/settings/NotificationsTab";
import { SettingsContentPanel, SettingsShell } from "@/components/settings/SettingsContentPanel";

interface ConnectionStatus {
  hasInstance: boolean;
  isConnected: boolean;
  qrCode?: string;
  instanceName?: string;
  loading: boolean;
  error?: string;
}

interface MenuItem {
  id: string;
  label: string;
  content: React.ReactNode;
}

const ProfileForm: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  
  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const validatePasswordForm = () => {
    if (!passwordData.currentPassword) {
      setMessage({ type: 'error', text: 'Senha atual é obrigatória' });
      return false;
    }
    if (!passwordData.newPassword) {
      setMessage({ type: 'error', text: 'Nova senha é obrigatória' });
      return false;
    }
    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Nova senha deve ter pelo menos 6 caracteres' });
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Nova senha e confirmação não coincidem' });
      return false;
    }
    return true;
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setMessage(null);

    try {
      if (!supabase) {
        throw new Error('Supabase não está configurado')
      }

      const { error } = await supabase
        .from('account_users')
        .update({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        })
        .eq('id', user.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      setMessage({ type: 'error', text: 'Erro ao atualizar perfil' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !validatePasswordForm()) return;

    setLoading(true);
    setMessage(null);

    try {
      // Nota: Validação de senha atual agora é feita pelo Supabase Auth
      // O campo 'password' foi removido de account_users após migração para auth.users

      if (!supabase) {
        throw new Error('Supabase não está configurado')
      }

      // Atualizar senha usando Supabase Auth
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      setMessage({ type: 'error', text: 'Erro ao alterar senha' });
    } finally {
      setLoading(false);
    }
  };

  const inputClassName =
    "h-11 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] dark:focus-visible:ring-[#2563EB]/30";

  return (
    <div className="max-w-3xl w-full flex flex-col gap-6">
      {message && (
        <div
          role="status"
          className={`p-3 rounded-xl border transition-colors duration-200 ${
            message.type === 'success'
              ? 'bg-[#ECFDF5] border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/30 dark:text-[#22C55E]'
              : 'bg-[#FEF2F2] border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/30 dark:text-[#F87171]'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Dados do Perfil */}
      <form onSubmit={handleUpdateProfile} className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-[#0F172A] border-b border-[#E5E7EB] pb-2 dark:text-[#FAFAFA] dark:border-[#3F3F464D]">
          Dados do Perfil
        </h3>

        <div className="form-group flex flex-col gap-3">
          <label htmlFor="name" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
            Nome
          </label>
          <Input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Nome"
            className={inputClassName}
            required
          />
        </div>

        <div className="form-group flex flex-col gap-3">
          <label htmlFor="email" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
            E-mail
          </label>
          <Input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="E-mail"
            className={inputClassName}
            required
          />
        </div>

        <div className="form-group flex flex-col gap-3">
          <label htmlFor="phone" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
            Telefone
          </label>
          <Input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Telefone"
            className={inputClassName}
            required
          />
        </div>

        <Button
          type="submit"
          className="rounded-xl bg-[#2563EB] px-5 text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8] disabled:bg-[#E5E7EB] disabled:text-[#94A3B8] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8] dark:disabled:bg-[#3F3F46] dark:disabled:text-[#71717A] min-w-[120px] mt-4 self-end"
          disabled={loading}
        >
          {loading ? 'Salvando...' : 'Salvar Perfil'}
        </Button>
      </form>

      {/* Alteração de Senha */}
      <div className="flex flex-col gap-4 border-t border-[#E5E7EB] pt-6 mt-6 dark:border-[#3F3F464D]">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <h3 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
            Alterar Senha
          </h3>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="min-w-[120px] rounded-xl border border-[#2563EB] bg-white text-[#2563EB] transition-colors duration-200 hover:bg-[#EEF4FF] dark:bg-transparent dark:border-[#60A5FA] dark:text-[#60A5FA] dark:hover:bg-blue-500/15"
          >
            {showPasswordForm ? 'Cancelar' : 'Alterar Senha'}
          </Button>
        </div>

        {showPasswordForm && (
          <form onSubmit={handleChangePassword} className="flex flex-col gap-4">
            <div className="form-group flex flex-col gap-3">
              <label htmlFor="currentPassword" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                Senha Atual
              </label>
              <Input
                type="password"
                id="currentPassword"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                placeholder="Digite sua senha atual"
                className={inputClassName}
                required
              />
            </div>

            <div className="form-group flex flex-col gap-3">
              <label htmlFor="newPassword" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                Nova Senha
              </label>
              <Input
                type="password"
                id="newPassword"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="Digite a nova senha (mín. 6 caracteres)"
                className={inputClassName}
                required
                minLength={6}
              />
            </div>

            <div className="form-group flex flex-col gap-3">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                Confirmar Nova Senha
              </label>
              <Input
                type="password"
                id="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="Confirme a nova senha"
                className={inputClassName}
                required
              />
            </div>

            <Button
              type="submit"
              className="rounded-xl bg-[#2563EB] px-5 text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8] disabled:bg-[#E5E7EB] disabled:text-[#94A3B8] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8] dark:disabled:bg-[#3F3F46] dark:disabled:text-[#71717A] min-w-[120px] mt-4 self-end"
              disabled={loading}
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

const menuItems: MenuItem[] = [
  {
    id: "conexao",
    label: "Conexão",
    content: <ConnectionTab />,
  },
  {
    id: "templates-wa",
    label: "Templates WhatsApp",
    content: <WhatsAppTemplatesTab />,
  },
  {
    id: "perfil",
    label: "Perfil",
    content: <ProfileForm />,
  },
  {
    id: "campos",
    label: "Campos personalizados",
    content: <CustomFieldsTab />,
  },
  {
    id: "tags",
    label: "Tags",
    content: <TagsTab />,
  },
  {
    id: "motivos-perda",
    label: "Motivos de perda",
    content: <LossReasonsTab />,
  },
  {
    id: "respostas",
    label: "Respostas Rápidas",
    content: <QuickRepliesTab />,
  },
  {
    id: "equipe",
    label: "Equipe",
    content: <TeamTab />,
  },
  {
    id: "notificacoes",
    label: "Notificações",
    content: <NotificationsTab />,
  },
  {
    id: "logs",
    label: "Logs",
    content: <LogsTab />,
  },
  {
    id: "integracoes",
    label: "Integrações",
    content: (
      <IntegrationsTab />
    ),
  },
  {
    id: "pipelines",
    label: "Pipelines",
    content: (
      <PipelinesTab />
    ),
  },
  {
    id: "reset",
    label: "Reset",
    content: <ResetTab />,
  },
];
const tabsWithAdd = ["logs"];
const Settings: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState<MenuItem>(menuItems[0]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      const foundItem = menuItems.find(item => item.id === tab);
      if (foundItem) {
        setSelectedItem(foundItem);
      }
    }
  }, [searchParams]);

  const [checkedItems, setCheckedItems] = useState({
    item1: false,
    item2: false,
    item3: false,
    item4: false,
    item5: false,
    item6: false,
    item7: false,
    item8: false,
    item9: false,
  });

  const itemNames = ["Dashboard", "Contatos", "Agenda", "CRM", "Chat", "Campanhas", "Assistentes", "Relatórios", "Configurações"];

  const handleCheckboxChange = (itemId: string, checked: boolean) => {
    setCheckedItems((prev) => ({
      ...prev,
      [itemId]: checked,
    }));
  };

  const AddNewDialog = () => {
    switch (selectedItem.id) {
      case "campos":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <form action="" className="w-full mt-4 gap-4 flex flex-col">
                  {/* name  */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Nome do campo</Label>
                    <Input
                      placeholder="Name"
                      id="name"
                      className="bg-neutral-600 text-sm placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Tipo</Label>
                    <Select>
                      <SelectTrigger className="bg-neutral-600 text-sm w-full placeholder:text-[#71717A] text-[#71717A]  !h-[40px] border focus:ring-white border-[#3F3F46]">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent className="">
                        <SelectGroup>
                          {TagOptions.map((item, index) => {
                            return (
                              <SelectItem key={index} value={item}>
                                {item}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* extra field   */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="extra-field">Descrição</Label>
                    <Textarea
                      rows={4}
                      cols={4}
                      placeholder="Descrição"
                      id="extra-field"
                      className="bg-neutral-600 min-h-[150px]  text-sm placeholder:text-[#71717A] text-[#71717A]   border focus:ring-white border-[#3F3F46]"
                    />
                  </div>

                  <div className="w-full flex justify-end">
                    <Button onClick={(e) => e.preventDefault()} className="w-[120px]">
                      Adicionar novo
                    </Button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        );
      case "tags":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <form action="" className="w-full mt-4 gap-4 flex flex-col">
                  {/* name  */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="tag">Nome do tag</Label>
                    <Input
                      placeholder="Lorem"
                      id="tag"
                      className="bg-neutral-600 text-sm placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>

                  <div className="w-full flex justify-end">
                    <Button onClick={(e) => e.preventDefault()} className="w-[120px]">
                      Adicionar novo
                    </Button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        );
      case "respostas":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <form action="" className="w-full mt-4 gap-4 flex flex-col">
                  {/* name  */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Nome do resposta</Label>
                    <Input
                      placeholder="Name"
                      id="name"
                      className="bg-neutral-600 text-sm placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>

                  {/* extra field   */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="extra-field">Descrição</Label>
                    <Textarea
                      rows={4}
                      cols={4}
                      placeholder="Descrição"
                      id="extra-field"
                      className="bg-neutral-600 min-h-[150px]  text-sm placeholder:text-[#71717A] text-[#71717A]   border focus:ring-white border-[#3F3F46]"
                    />
                  </div>
                  <div className="w-full flex justify-end">
                    <Button onClick={(e) => e.preventDefault()} className="w-[120px]">
                      Adicionar novo
                    </Button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        );
      case "equipe":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <form action="" className="w-full mt-4 gap-4 flex flex-col">
                  {/* name  */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Nome do campo</Label>
                    <Input
                      placeholder="Name"
                      id="name"
                      className="bg-neutral-600 text-sm placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">E-mail</Label>
                    <Input
                      placeholder="E-mail"
                      id="email"
                      className="bg-neutral-600 text-sm placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="email"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Telefone</Label>
                    <Input
                      placeholder="Telefone"
                      id="number"
                      className="bg-neutral-600 text-sm placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="number"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Senha</Label>
                    <Input
                      placeholder="Senha "
                      id="senha"
                      className="bg-neutral-600 text-sm placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Todos os contatos</Label>
                    <Select>
                      <SelectTrigger className="bg-neutral-600 !h-[38px] text-sm w-full placeholder:text-[#71717A] text-[#71717A]   border focus:ring-white border-[#3F3F46]">
                        <SelectValue placeholder="Todos os contatos" />
                      </SelectTrigger>
                      <SelectContent className=" ">
                        <SelectGroup>
                          {TagOptions.map((item, index) => {
                            return (
                              <SelectItem key={index} value={item}>
                                {item}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className=" flex flex-col gap-4">
                    <p className=" underline text-sm font-light text-white">Selecionar todos</p>
                    <div className=" grid grid-cols-2 max-w-[250px] gap-3">
                      {Object.entries(checkedItems).map(([itemId, isChecked], index) => (
                        <div key={itemId} className="flex items-center    gap-3">
                          <Checkbox
                            id={itemId}
                            checked={isChecked}
                            onCheckedChange={(checked) => handleCheckboxChange(itemId, checked === true)}
                            className=" bg-white"
                          />
                          <label
                            htmlFor={itemId}
                            className="text-sm text-white font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {itemNames[index]}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="w-full flex justify-end">
                    <Button onClick={(e) => e.preventDefault()} className="w-[120px]">
                      Adicionar novo
                    </Button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        );
      case "logs":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg"> </DialogContent>
          </Dialog>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full flex h-[calc(100dvh-110px)] flex-col gap-6 bg-[#F8FAFC] text-[#0F172A] dark:bg-[#18181B] dark:text-[#FAFAFA]">
      <h2 className="text-2xl xl:text-3xl font-bold tracking-tight text-[#0F172A] dark:text-[#FAFAFA]">
        Configurações
      </h2>
      <SettingsShell>
        <div className="w-[187px] shrink-0 md:w-[220px]">
          <nav className="space-y-1">
            {menuItems.map((item) => (
              <Button
                key={item.id}
                variant="ghost"
                className={`w-full justify-start text-left text-sm h-auto rounded-xl p-3 transition-colors duration-200 ${
                  selectedItem.id === item.id
                    ? "bg-[#EEF4FF] text-[#2563EB] hover:bg-[#EEF4FF] hover:text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA] dark:border dark:border-blue-500/30 dark:hover:bg-blue-500/20 dark:hover:text-[#60A5FA]"
                    : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#2563EB] dark:text-[#A1A1AA] dark:hover:bg-[#3F3F46] dark:hover:text-[#60A5FA]"
                }`}
                onClick={() => setSelectedItem(item)}
              >
                {item.label}
              </Button>
            ))}
          </nav>
        </div>

        <div className="flex-1 min-h-0 min-w-0 h-full @container/settings flex flex-col">
          <SettingsContentPanel className="flex flex-1 min-h-0 flex-col overflow-hidden">
            <div className="mb-6 flex flex-col @sm/settings:flex-row @sm/settings:items-center justify-between gap-3 shrink-0">
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] dark:text-[#FAFAFA]">
                {selectedItem.label}
              </h1>
              {tabsWithAdd.includes(selectedItem.id) && <AddNewDialog />}
            </div>
            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
              {selectedItem.content}
            </div>
            {tabsWithAdd.includes(selectedItem.id) && (
              <Button
                type="submit"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white min-w-[120px] mt-6 self-end rounded-xl shadow-sm transition-colors duration-200"
              >
                Salvar
              </Button>
            )}
          </SettingsContentPanel>
        </div>
      </SettingsShell>
    </div>
  );
};

export default Settings;